import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, X, Check } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { DiveTurnResult, ToolDive } from '../../../types/aai';

// The dive's "hand your assistant one real task", live. Every turn goes to
// /my/tool-dives/{id}/try-turn: the assistant's hands are the caller's OWN
// delegation answers on this tool, executed through their own connection.
// 'Approval' decisions and the act tier pause here with the exact call for
// per-run consent before anything executes.
type TryMsg = { role: 'user' | 'assistant'; content: string };

const TryToolModal: React.FC<{
  dive: ToolDive;
  onClose: () => void;
  onActionRan: () => void;
  onSaveNote: (note: string) => Promise<void> | void;
}> = ({ dive, onClose, onActionRan, onSaveNote }) => {
  const [messages, setMessages] = useState<TryMsg[]>([]);
  const [approved, setApproved] = useState<string[]>([]);
  const [pending, setPending] = useState<DiveTurnResult['pending_approval']>(null);
  const [offered, setOffered] = useState<DiveTurnResult['offered'] | null>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const ranRef = useRef(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  // Ref-held so a parent re-render (step tick -> tracker reload -> new inline
  // callback identity) doesn't recreate `turn` and re-fire the greeting
  // effect, which would wipe the transcript mid-conversation.
  const onActionRanRef = useRef(onActionRan);
  onActionRanRef.current = onActionRan;

  const turn = useCallback(async (history: TryMsg[], approvedNow: string[]) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await aaiApi.runDiveTurn(dive.id, history, approvedNow);
      setMessages([...history, { role: 'assistant', content: data.reply }]);
      setPending(data.pending_approval);
      setOffered(data.offered);
      if (!ranRef.current && data.tool_activity.some((a) => a.ok)) {
        ranRef.current = true;
        onActionRanRef.current();
      }
    } catch {
      setError('That turn hit a snag. Send it again.');
      setMessages(history);
    } finally { setBusy(false); }
  }, [dive.id]);

  useEffect(() => { void turn([], []); }, [turn]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy, pending]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setPending(null);
    const history = [...messages, { role: 'user' as const, content: text }];
    setMessages(history);
    await turn(history, approved);
  };
  const approve = async () => {
    if (!pending) return;
    const next = [...approved, pending.key];
    setApproved(next);
    setPending(null);
    await turn(messages, next);
  };
  const saveNote = async () => {
    const text = note.trim();
    if (!text) return;
    setSaving(true);
    try {
      await onSaveNote(text);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally { setSaving(false); }
  };

  const actionCount = offered ? offered.actions.length : 0;
  return (
    <div className="fixed inset-0 z-[195] bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
          <h2 className="text-base font-semibold text-gray-900">One real task · {dive.tool_name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="mb-2">
          {offered && offered.connected && actionCount > 0 && (
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-0.5">
              {actionCount} action{actionCount === 1 ? '' : 's'} delegated · your own {dive.tool_name} connection
            </span>
          )}
          {offered && !offered.connected && (
            <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">
              Not connected yet, chat works without hands
            </span>
          )}
        </div>
        {pending && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-2">
            <p className="text-[12.5px] text-amber-800 font-semibold mb-1">
              Approve this before it runs: {pending.label}
            </p>
            {pending.why && <p className="text-[11.5px] text-amber-800/80 mb-2">{pending.why}</p>}
            {/* The plain-language version of the call. The raw provider JSON is
                only a fallback for an action whose arguments we can't name. */}
            {pending.summary && pending.summary.length > 0 && (
              <dl className="text-[11.5px] bg-white/60 rounded px-2 py-1.5 mb-2 max-h-32 overflow-y-auto space-y-0.5">
                {pending.summary.map((item, i) => (
                  <div key={i} className="flex gap-1.5">
                    <dt className="text-amber-900/60 font-semibold shrink-0">{item.label}</dt>
                    <dd className="text-amber-900 min-w-0 whitespace-pre-wrap break-words">{item.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {(!pending.summary || pending.summary.length === 0)
              && pending.args_preview && pending.args_preview !== '{}' && (
              <pre className="text-[11px] text-amber-900/80 bg-white/60 rounded px-2 py-1.5 mb-2 whitespace-pre-wrap break-all max-h-28 overflow-y-auto">{pending.args_preview}</pre>
            )}
            <div className="flex items-center gap-2">
              <button onClick={approve} disabled={busy}
                className="text-[12px] font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg px-3 py-1.5 disabled:opacity-50">
                Allow and run it
              </button>
              <button onClick={() => setPending(null)} disabled={busy}
                className="text-[12px] font-semibold text-amber-700 hover:text-amber-900 disabled:opacity-50">
                Not now, I'll type instead
              </button>
            </div>
          </div>
        )}
        <div className="h-[340px] overflow-y-auto space-y-2.5 bg-gray-50 rounded-lg p-3 mb-2.5">
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-brand-500 text-white' : 'bg-white border border-gray-100 text-gray-800'}`}>
                {m.content}
              </div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="bg-white border border-gray-100 rounded-lg px-3 py-2"><Loader2 size={14} className="animate-spin text-gray-400" /></div></div>}
          {error && <p className="text-[12px] text-red-500">{error}</p>}
          <div ref={endRef} />
        </div>
        <div className="flex items-end gap-2">
          <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            placeholder="A real task from your week…"
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
          <button onClick={send} disabled={busy || !input.trim()}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-4 py-2 disabled:opacity-50 whitespace-nowrap">
            Send
          </button>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-[11.5px] text-gray-500 mb-1.5">Task done? Write down what worked. It lands in your dive notes and becomes your first skill.</p>
          <div className="flex items-center gap-1.5">
            <input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="What worked?"
              className="flex-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
            <button onClick={saveNote} disabled={saving || !note.trim()}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2.5 py-1 text-[12px] font-medium disabled:opacity-50 whitespace-nowrap">
              {saving && <Loader2 size={12} className="animate-spin" />}
              {saved && <Check size={12} />}
              {saved ? 'Saved' : 'Save to notes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TryToolModal;
